import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { tap } from 'rxjs/operators';

import { environment } from "../../environments/environment";
import { Logger } from "../global/logger";
import { UserClaim } from "./user-claim";
import { GetObjectResponse } from '../global/responses/get-object-response';
import { ExecuteObservable } from '../global/execute-observable';
import { WebServiceResponse } from '../global/responses/web-service-response';
import { UserClaimTypesEnum } from './user-claim-types.enum';

/**
 * This class is the service used to communicate authentication data to the server.
 */
@Injectable()
export class AuthenticationService {

// #region [ Fields ]

	/** The path to the authentication controller. */
	private static readonly AuthenticationControllerPath: string = "Authentication/";

	/** Indicates if the current session is authenticated. */
	private _Authenticated: boolean = false;

	/** The user claims associated with the current session. */
	private _UserClaims: UserClaim[] = null;
	
// #region [ Constructor ]

	constructor(
		private _HttpClient: HttpClient
	) { }

// #region [ Properties ]

	/**
	 * Indicates if the current session is authenticated.
	 */
	public get Authenticated(): boolean {
		return this._Authenticated;
	}

	/**
	 * The user claims associated with the current session. */
	public get UserClaims(): UserClaim[] {
		return this._UserClaims;
	}

	/** The user identifier of the logged in user*/
	public get UserIdentifier(): string {
		let userId: string;
		if (this._UserClaims != null) {
			for (let intIndex = 0; intIndex < this._UserClaims.length; intIndex++)
				if (this._UserClaims[intIndex].Type == "Id") {
					userId = this._UserClaims[intIndex].Value;
				}
		}

		return userId;
	}
	
// #region [ Public Methods ]

	/**
	 * Passes credentials to the web service for validation.
	 *
	 * @param UserName The user name supplied for authentication.
	 * @param Password The password supplied for authentication.
	 * @returns An observable to an authentication response that indicates if the credentials were accepted or not and the user claims for the session.
	 */
	public Login(UserName: string, Password: string): Promise<UserClaim[]> {
		let objObservable: Observable<GetObjectResponse<UserClaim[]>>;
		let strUrl: string;
		let intIdentifier: number;
		let objCredentials: object;

		// Build the request.
		strUrl = environment.WebServiceUrl + AuthenticationService.AuthenticationControllerPath + "Login";
		objCredentials = { UserName: UserName, Password: Password };
		objObservable = this._HttpClient.post<GetObjectResponse<UserClaim[]>>(strUrl, objCredentials, { withCredentials: true });

		// Setup logging and process response.
		intIdentifier = Logger.ServiceStartLog("POST", strUrl, null);
		objObservable = objObservable.pipe(tap(
			(objResponse: GetObjectResponse<UserClaim[]>) => {
				Logger.ServiceEndLog(intIdentifier, "POST", strUrl, objResponse);
				if (objResponse.OperationSucceeded) {
					this._UserClaims = objResponse.Result;
					this._Authenticated = true;
				}
				else {
					this._UserClaims = null;
					this._Authenticated = false;
				}
			}
		));

		return ExecuteObservable.ExecuteQuery<UserClaim[]>(objObservable, "AuthenticationService.Login");
	}

	/**
	 * Gets the authentication flag and user claims for the current session.
	 *
	 * @returns An observable to an authentication response indicating the user claims for the session.
	 */
	public GetUserClaims(): Promise<UserClaim[]> {
		let objObservable: Observable<GetObjectResponse<UserClaim[]>>;
		let strUrl: string;
		let intIdentifier: number;

		// Build the request.
		strUrl = environment.WebServiceUrl + AuthenticationService.AuthenticationControllerPath + "GetUserClaims";
		objObservable = this._HttpClient.get<GetObjectResponse<UserClaim[]>>(strUrl, { withCredentials: true });

		// Setup logging.
		intIdentifier = Logger.ServiceStartLog("GET", strUrl, null);
		objObservable = objObservable.pipe(tap(
			(objResponse: GetObjectResponse<UserClaim[]>) => {
				Logger.ServiceEndLog(intIdentifier, "GET", strUrl, objResponse);
				if (objResponse.OperationSucceeded) {
					this._UserClaims = objResponse.Result;
					this._Authenticated = true;
				}
				else {
					this._UserClaims = null;
					this._Authenticated = false;
				}
			}
		));

		return ExecuteObservable.ExecuteQuery<UserClaim[]>(objObservable, "AuthenticationService.GetUserClaims");
	}

	/**
	 * Loads and returns the user claims if they haven't been loaded or just returns the previously loaded claims if they are in memory.
	 *
	 * @returns A promise to the user's claims.
	 */
	public LoadUserClaims(): Promise<UserClaim[]> {
		return new Promise<UserClaim[]>((objResolve: Function, objReject: Function) => {
			if (!this.UserClaims)
				this.GetUserClaims()
					.then((objClaims: UserClaim[]) => objResolve(objClaims))
					.catch(() => objReject());
			else
				objResolve(this.UserClaims);
		});
	}

	/**
	 * Searches the user claims currently in the service for a specific claim value.
	 *
	 * @param ClaimType The claim type to return the value for.
	 * @returns A string value for the claim, or null if it was not found.
	 */
	public GetUserClaimValue(ClaimType: UserClaimTypesEnum): string {
		let objClaim: UserClaim;

		objClaim = null;
		if (this.UserClaims != null)
			for (let intIndex = 0; intIndex < this.UserClaims.length && objClaim == null; intIndex++)
				if (this.UserClaims[intIndex].Type == ClaimType)
					objClaim = this.UserClaims[intIndex];

		if (objClaim != null)
			return objClaim.Value;
		else
			return null;
	}

	/**
	 * Ends a user's session on the server.
	 */
	public Logout(): Promise<void> {
		let objObservable: Observable<WebServiceResponse>;
		let strUrl: string;
		let intIdentifier: number;

		// Build the request.
		strUrl = environment.WebServiceUrl + AuthenticationService.AuthenticationControllerPath + "Logout";
		objObservable = this._HttpClient.post<WebServiceResponse>(strUrl, null, { withCredentials: true });

		// Setup logging and process response.
		intIdentifier = Logger.ServiceStartLog("POST", strUrl, null);
		objObservable = objObservable.pipe(tap(
			() => {
				Logger.ServiceEndLog(intIdentifier, "POST", strUrl, null);
				this._Authenticated = false;
				this._UserClaims = null;
			}
		));

		return ExecuteObservable.ExecuteVoid(objObservable, "AuthenticationService.Logout");
	}

	/**
	 * Changes the authentication state to false
	 */
	public Deauthenticate(): void {
		this._Authenticated = false;
	}  
}